import {
  type Form,
  type FormField,
  ValidationFormat,
  type MultilineAddress,
} from '@wix/ambassador-forms-v4-form/types';
import { getCountryByShortKey } from '@wix/locale-dataset-javascript';

import { applyValidationProperties } from './apply-validation-properties';
import { applyViewProperties } from './apply-view-properties';
import { ADDRESS_FORM_ID_BY_COUNTRY, DEFAULT_COUNTRY } from './constants';
import { COUNTRY_CODES, type CountryCode } from './country-codes';
import { getCountryField } from './get-country-field';
import { mapSubdivisionReplacedBy } from './map-subdivision-replaced-by';

type AddressFormIdByCountryMap = typeof ADDRESS_FORM_ID_BY_COUNTRY;
type AddressFormByCountryMap = { [country: string]: Form };

export class MultilineAddressToolkit {
  private readonly addressFormByCountry: AddressFormByCountryMap;

  constructor(
    addressForms: Form[],
    addressFormIdByCountry: typeof ADDRESS_FORM_ID_BY_COUNTRY = ADDRESS_FORM_ID_BY_COUNTRY,
  ) {
    this.addressFormByCountry = this.mapFormsByCountry(
      addressFormIdByCountry,
      addressForms,
    );
  }

  getFormByCountry(country?: string, rootFormField?: FormField): Form {
    this.assertMultiLineAddressField(rootFormField);

    const addressForm =
      country && this.addressFormByCountry[country]
        ? this.addressFormByCountry[country]
        : this.addressFormByCountry[DEFAULT_COUNTRY];

    if (!addressForm) {
      throw new Error(`No form suitable fo country: ${country}`);
    }

    return rootFormField
      ? this.addCountryField(
          this.addSubdivisions(
            this.applyRootFormSettingsOverrides(addressForm, rootFormField),
            country,
          ),
          rootFormField,
        )
      : addressForm;
  }

  /**
   * Normalizes empty values in submission to empty objects
   * This is needed to pass validation when address is required
   * @param submission - submission to normalize
   * @param form - form to get fields from
   */
  normalizeEmptyValues = (
    submission: Record<string, unknown>,
    form: Form,
  ): Record<string, unknown> => {
    return Object.keys(submission).reduce<Record<string, unknown>>(
      (acc, key) => {
        const field = form.fields?.find((_field) => _field.target === key);
        if (
          field?.validation?.required &&
          !field?.hidden &&
          field?.validation?.predefined?.format ===
            ValidationFormat.MULTILINE_ADDRESS
        ) {
          acc[key] = submission[key] || {};
        } else {
          acc[key] = submission[key];
        }

        return acc;
      },
      {},
    );
  };

  addCountryField = (form: Form, rootFormField: FormField) => {
    return {
      ...form,
      fields: [
        getCountryField(
          rootFormField.validation?.required && !rootFormField?.hidden,
          rootFormField.validation?.predefined?.multilineAddressOptions,
        ),
        ...(form.fields ?? []),
      ],
    };
  };

  addSubdivisions = (form: Form, countryCode?: string) => {
    if (!countryCode) {
      return form;
    }

    const country = getCountryByShortKey(countryCode);
    const countryCodeAlpha3 =
      countryCode && countryCode in COUNTRY_CODES
        ? COUNTRY_CODES[countryCode as CountryCode]
        : undefined;
    const subdivisions = mapSubdivisionReplacedBy(
      country?.subdivisions?.list || [],
      countryCodeAlpha3!,
    );

    if (!subdivisions?.length) {
      return form;
    }

    return {
      ...form,
      fields: form.fields?.map((field) => {
        if (field?.target === 'subdivision') {
          return {
            ...field,
            view: {
              ...field.view,
              options: subdivisions.map((subdivision) => ({
                value: subdivision.key,
                label: subdivision.displayName,
              })),
            },
            validation: {
              ...field.validation,
              string: {
                ...field.validation?.string,
                enum: subdivisions.map((subdivision) => subdivision.key),
              },
            },
          };
        }
        return field;
      }),
    };
  };

  /**
   * Adds hardcoded country field, which is needed to build validation.
   * Address forms themselves not contain country field
   * @param rootFormField - root form field
   * @param submissionValue - value of submission, to get country info
   */
  getFormForValidation = (
    rootFormField: FormField,
    submissionValue?: unknown,
  ): Form => {
    const country = this.getCountry(submissionValue);
    return this.getFormByCountry(country, rootFormField);
  };

  private applyRootFormSettingsOverrides(
    addressForm: Form,
    rootFormField: FormField,
  ) {
    const modifications: ((form: Form) => Form)[] = rootFormField
      ? [
          applyViewProperties(rootFormField.view as MultilineAddress),
          applyValidationProperties(
            rootFormField.validation?.predefined?.multilineAddressOptions,
          ),
        ]
      : [];
    return modifications.reduce((acc, func) => func(acc), addressForm);
  }

  private assertMultiLineAddressField(rootFormField?: FormField) {
    if (
      rootFormField &&
      rootFormField.validation?.predefined?.format !==
        ValidationFormat.MULTILINE_ADDRESS
    ) {
      throw new Error(
        `Only field ${ValidationFormat.MULTILINE_ADDRESS} is supported`,
      );
    }
  }

  private mapFormsByCountry(
    formIdByCountryMap: AddressFormIdByCountryMap,
    addressForms: Form[],
  ): AddressFormByCountryMap {
    const formsById = addressForms.reduce<{ [id: string]: Form }>(
      (acc, form) => {
        acc[form.id!] = form;
        return acc;
      },
      {},
    );

    return Object.fromEntries(
      Object.entries(formIdByCountryMap).map(([country, formId]) => {
        const form = formsById[formId];
        if (form) {
          return [country, form];
        } else {
          throw new Error(`Missing form template for country ${country}`);
        }
      }),
    );
  }

  private getCountry(value: unknown): string {
    if (value && typeof value === 'object' && 'country' in value) {
      return (value as { country: string }).country;
    }
    return DEFAULT_COUNTRY;
  }
}
